import {
  forwardRef,
  memo,
  RefObject,
  UIEvent,
  useImperativeHandle,
  useRef,
} from 'react';
import { isEqual } from 'lodash';
import {
  BodyCell,
  BodyCellItemsContainer,
  ComponentMatrixBodyContainer,
} from './atoms';
import { ComponentItem } from './componentItem';
import { ColumnResizeHandle, RowResizeHandle } from './resizeHandle';
import { ArdoqId } from '@ardoq/api-types';
import {
  BodyCellModel,
  ColumnResizeHandleModel,
  ComponentMatrix2Commands,
  RowElementModel,
  UserAdjustedColumnWidths,
  UserAdjustedRowHeights,
} from '../types';

type ComponentMatrixBodyProperties = {
  commands: ComponentMatrix2Commands;
  rowModels: RowElementModel[];
  columnWidths: number[];
  rowHeights: number[];
  contextComponentId: ArdoqId | null;
  hoveredComponentId: ArdoqId | null;
  focusedComponentId: ArdoqId | null;
  onScroll: (event: UIEvent<HTMLDivElement>) => void;
  /** Pre-built per-leaf-column resize-handle props (ARD-31416 follow-up:
   * built at view-model assembly so the body stays a pure mapper). */
  resizeHandles: ColumnResizeHandleModel[];
  userAdjustedColumnWidths: UserAdjustedColumnWidths;
  /** Per-row stable keys aligned with rowHeights (ARD-31416). */
  leafRowKeys: string[];
  /** Measured minimums per row; the row resize handle clamps drag against these. */
  leafRowMinHeights: number[];
  /** Current persisted user adjustments for row heights. */
  userAdjustedRowHeights: UserAdjustedRowHeights;
};

const areResizeHandlesEqual = (
  a: ColumnResizeHandleModel[],
  b: ColumnResizeHandleModel[]
) =>
  a.length === b.length &&
  a.every((handle, index) => {
    const next = b[index];
    return (
      handle.columnKey === next.columnKey &&
      handle.gridColumn === next.gridColumn &&
      handle.gridRowEnd === next.gridRowEnd &&
      handle.currentWidth === next.currentWidth &&
      handle.minWidth === next.minWidth
    );
  });

const isBodyCellEffectivelyEqual = (a: BodyCellModel, b: BodyCellModel) => {
  return (
    a.columnIndex === b.columnIndex &&
    a.columnSpan === b.columnSpan &&
    a.rowSpan === b.rowSpan &&
    isEqual(a.items, b.items)
  );
};
const isRowModelEffectivelyEqual = (a: RowElementModel, b: RowElementModel) => {
  return (
    a.bodyCells.length === b.bodyCells.length &&
    a.bodyCells.every((bodyCell, bodyCellIndex) =>
      isBodyCellEffectivelyEqual(bodyCell, b.bodyCells[bodyCellIndex])
    )
  );
};

const hasComponentId = (
  rowModels: RowElementModel[],
  componentId: string | null
) =>
  componentId !== null &&
  rowModels.some(rowModel =>
    rowModel.bodyCells.some(bodyCell =>
      bodyCell.items.some(item => item.componentId === componentId)
    )
  );

export type ComponentMatrixBodyRef = {
  scrollBy: (scrollLeft: number, scrollTop: number) => void;
  container: RefObject<HTMLDivElement | null>;
};
const ComponentMatrixBody = forwardRef<
  ComponentMatrixBodyRef,
  ComponentMatrixBodyProperties
>((props, ref) => {
  const container = useRef<HTMLDivElement>(null);
  const scrollBy = (scrollLeft: number, scrollTop: number) => {
    if (!container.current) {
      return;
    }
    container.current.scrollBy(scrollLeft, scrollTop);
  };
  useImperativeHandle(ref, () => ({ scrollBy, container }));
  const {
    commands,
    rowModels,
    columnWidths,
    rowHeights,
    onScroll,
    hoveredComponentId,
    focusedComponentId,
    resizeHandles,
    userAdjustedColumnWidths,
    leafRowKeys,
    leafRowMinHeights,
    userAdjustedRowHeights,
  } = props;
  // Render N-1 row handles, one between each pair of adjacent rows. Same
  // pattern as the column handles, rotated 90°: handle sits in row N and
  // overflows 3px down via its absolute ::before so it lands on the boundary
  // with row N+1.
  //
  // `currentHeight` is the *content* height (excluding any `rowBottomGap`
  // encoded into the grid track for inter-root-group spacing). The persisted
  // value is also the content height, so the bottom gap is preserved across
  // drag/release without compounding.
  //
  // TODO(ARD-31416 follow-up): lift these into the view-model too, mirroring
  // the column-handle lift in `resizeHandles`. Kept inline here so this
  // rebase doesn't expand scope.
  const rowHandles =
    rowHeights.length > 1
      ? rowHeights.slice(0, -1).map((trackHeight, index) => {
          const bottomGap = rowModels[index]?.rowBottomGap ?? 0;
          const contentHeight = trackHeight - bottomGap;
          return (
            <RowResizeHandle
              key={`row-${leafRowKeys[index] ?? index}`}
              gridRow={index + 1}
              gridColumnEnd={columnWidths.length + 1}
              currentHeight={contentHeight}
              minHeight={leafRowMinHeights[index] ?? contentHeight}
              onCommit={height =>
                commands.onRowResize(
                  userAdjustedRowHeights,
                  leafRowKeys[index] ?? '',
                  height
                )
              }
              onReset={() =>
                commands.onRowResizeHandleDoubleClick(
                  userAdjustedRowHeights,
                  leafRowKeys[index] ?? ''
                )
              }
            />
          );
        })
      : null;
  return (
    <ComponentMatrixBodyContainer
      ref={container}
      style={{
        gridTemplateColumns: `${columnWidths
          .map(value => `${value}px`)
          .join(' ')}`,
        gridTemplateRows: rowHeights.map(height => `${height}px`).join(' '),
      }}
      onScroll={onScroll}
      onWheel={e => e.stopPropagation()}
    >
      {resizeHandles.map(handle => (
        <ColumnResizeHandle
          key={handle.columnKey}
          {...handle}
          onCommit={width =>
            commands.onColumnResize(
              userAdjustedColumnWidths,
              handle.columnKey,
              width
            )
          }
          onReset={() =>
            commands.onColumnResizeHandleDoubleClick(
              userAdjustedColumnWidths,
              handle.columnKey
            )
          }
        />
      ))}
      {rowHandles}
      {rowModels.map((rowModel, rowIndex) =>
        rowModel.bodyCells.map((bodyCell, bodyCellIndex) => {
          const isFirstRow = rowIndex === 0;
          const isLastRow = rowIndex === rowModels.length - 1;
          const isRootGroupBoundary = (rowModel.rowBottomGap ?? 0) > 0;
          return (
            <BodyCell
              key={`${rowIndex}.${bodyCellIndex}`}
              style={{
                gridColumn: `${bodyCell.columnIndex + 1} / ${
                  bodyCell.columnIndex + 1 + bodyCell.columnSpan
                }`,
                gridRow: `${rowIndex + 1} / ${rowIndex + 1 + bodyCell.rowSpan}`,
              }}
            >
              <BodyCellItemsContainer
                $isFirstRow={isFirstRow}
                $isLastRow={isLastRow}
                $isRootGroupBoundary={isRootGroupBoundary}
              >
                {bodyCell.items.map(
                  (
                    {
                      componentId,
                      componentLabel,
                      fillColor,
                      hoverFillColor,
                      isDark,
                      isContext,
                      representationData,
                      urlFieldValues,
                      otherLabels,
                      otherLabelWrappedLines,
                    },
                    itemIndex
                  ) => (
                    <ComponentItem
                      key={`${rowIndex}.${bodyCellIndex}.${itemIndex}`}
                      componentId={componentId}
                      fillColor={fillColor}
                      hoverFillColor={hoverFillColor}
                      isDark={isDark}
                      isContext={isContext}
                      representationData={representationData}
                      componentLabel={componentLabel}
                      urlFieldValues={urlFieldValues}
                      otherLabels={otherLabels}
                      otherLabelWrappedLines={otherLabelWrappedLines}
                      isHovered={componentId === hoveredComponentId}
                      isSelected={componentId === focusedComponentId}
                      commands={commands}
                    />
                  )
                )}
              </BodyCellItemsContainer>
            </BodyCell>
          );
        })
      )}
    </ComponentMatrixBodyContainer>
  );
});

export const componentMatrixBody = memo(
  ComponentMatrixBody,
  (props, nextProps) =>
    !(
      props.commands !== nextProps.commands ||
      props.rowModels.length !== nextProps.rowModels.length ||
      props.columnWidths.length !== nextProps.columnWidths.length ||
      props.rowHeights.length !== nextProps.rowHeights.length ||
      (props.contextComponentId !== nextProps.contextComponentId &&
        (hasComponentId(props.rowModels, props.contextComponentId) ||
          hasComponentId(nextProps.rowModels, nextProps.contextComponentId))) ||
      (props.hoveredComponentId !== nextProps.hoveredComponentId &&
        (hasComponentId(props.rowModels, props.hoveredComponentId) ||
          hasComponentId(nextProps.rowModels, nextProps.hoveredComponentId))) ||
      (props.focusedComponentId !== nextProps.focusedComponentId &&
        (hasComponentId(props.rowModels, props.focusedComponentId) ||
          hasComponentId(nextProps.rowModels, nextProps.focusedComponentId))) ||
      !isEqual(props.rowHeights, nextProps.rowHeights) ||
      !isEqual(props.columnWidths, nextProps.columnWidths) ||
      !areResizeHandlesEqual(props.resizeHandles, nextProps.resizeHandles) ||
      !isEqual(
        props.userAdjustedColumnWidths,
        nextProps.userAdjustedColumnWidths
      ) ||
      !isEqual(
        props.userAdjustedRowHeights,
        nextProps.userAdjustedRowHeights
      ) ||
      !isEqual(props.leafRowKeys, nextProps.leafRowKeys) ||
      props.rowModels.some(
        (rowModel, rowModelIndex) =>
          !isRowModelEffectivelyEqual(
            rowModel,
            nextProps.rowModels[rowModelIndex]
          )
      )
    )
);
